import React, { useState, useRef, Fragment, useEffect, useCallback } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl-next';
import { styled } from '@compiled/react';

import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import type { ModalHeaderProps as HeaderComponentProps } from '@atlaskit/modal-dialog';
import Modal, { ModalHeader, ModalTitle, ModalBody } from '@atlaskit/modal-dialog';
import Tabs, { Tab, TabList } from '@atlaskit/tabs';
import LoadingButton from '@atlaskit/button/loading-button';
import EditorCloseIcon from '@atlaskit/icon/glyph/editor/close';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { N20, N300 } from '@atlaskit/theme/colors';
import Spinner from '@atlaskit/spinner/spinner';

import type { ImagePickerModalComponentProps } from '../pageCoverPictureTypes';
import {
	MODAL_HEIGHT,
	MODAL_WIDTH,
	SMALL_MODAL_WIDTH,
	DEFAULT_QUERY_WORD,
	UNSPLASH_LICENSE_LINK,
	UNSPLASH_TERMS_OF_SERVICE_LINK,
	MAX_WINDOW_WIDTH_BEFORE_OVERFLOW,
} from '../pageCoverPictureConstants';

import { UnsplashImageSelection } from './UnsplashImageSelection';
import { UnsplashSearchBar } from './UnsplashSearchBar';
import { getUnsplashPhotos } from './unsplashAPI';

const i18n = defineMessages({
	updateImageModalTitle: {
		id: 'page-cover-picture.update.header.image.modal.title',
		description: 'Title in modal which informs users that they can update their header image.',
		defaultMessage: 'Update header image',
	},
	searchTab: {
		id: 'page-cover-picture.search.tab',
		description: 'Search tab title in the cover picture modal.',
		defaultMessage: 'Search',
	},
	uploadTab: {
		id: 'page-cover-picture.upload.tab',
		description: 'Upload tab title in the cover picture modal.',
		defaultMessage: 'Upload',
	},
	loadMore: {
		id: 'page-cover-picture.load.more.unsplash.photos',
		description: 'Text in button user can click to load more Unsplash images.',
		defaultMessage: 'Load more',
	},
	unsplashLegal: {
		id: 'page-cover-picture.unsplash.legal',
		defaultMessage:
			'By using images from Unsplash, you agree to their {unsplashLicenseLink} and {unsplashTermsOfServiceLink}.',
		description: 'Text that explains the Unsplash legal agreements (license and terms of service)',
	},
	unsplashLicenseLink: {
		id: 'page-cover-picture.unsplash.license',
		description: 'Text that links to Unsplash license',
		defaultMessage: 'license',
	},
	unsplashTermsOfServiceLink: {
		id: 'page-cover-picture.unsplash.terms.of.service.',
		description: 'Text that links to Unsplash Terms of Service',
		defaultMessage: 'Terms of Service',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ModalCloseIconWrapper = styled.div({
	cursor: 'pointer',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SearchBarWrapper = styled.div({
	gridColumnStart: 'span 2',
	marginBottom: token('space.200', '16px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const UnsplashLegalTerms = styled.div({
	width: '100%',
	backgroundColor: token('color.background.accent.gray.subtlest', N20),
	borderBottomLeftRadius: '3px',
	borderBottomRightRadius: '3px',
	color: token('color.text.subtle', N300),
	fontSize: '12px',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	padding: `${token('space.100', '8px')} 0`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LoadMoreButtonWrapper = styled.div({
	display: 'flex',
	justifyContent: 'center',
	padding: `${token('space.100', '8px')} 0`,
	gridColumnStart: 'span 2',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HeaderWrapper = styled.div({
	display: 'flex',
	flexDirection: 'column',
	width: '100%',
});

const tabsWrapperStyles = xcss({
	marginTop: 'space.100',
	marginBottom: 'space.negative.100',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SpinnerWrapper = styled.div({
	display: 'flex',
	justifyContent: 'center',
	marginTop: token('space.250', '20px'),
	gridColumnStart: 'span 2',
});

type ModalContentWrapperProps = {
	windowWidth: number;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ModalContentWrapper = styled.div<ModalContentWrapperProps>({
	display: 'grid',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	gridTemplateColumns: ({ windowWidth }) =>
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		windowWidth > MAX_WINDOW_WIDTH_BEFORE_OVERFLOW ? 'auto auto auto' : 'auto auto',
	gridGap: '8px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TitleDiv = styled.div({
	display: 'flex',
	justifyContent: 'space-between',
	width: '100%',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const UnderlinedLink = styled.a({
	textDecoration: 'underline',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
	color: `${token('color.text.subtlest', N300)} !important`, // Using color.text.subtlest here because color.link is blue
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LegalTextWrapper = styled.div({
	margin: `0 ${token('space.100', '8px')}`,
});

const UnsplashLicenseLink = ({ link }) => (
	<UnderlinedLink target="_blank" href={link}>
		<FormattedMessage {...i18n.unsplashLicenseLink} />
	</UnderlinedLink>
);

const UnsplashTermsOfServiceLink = ({ link }) => (
	<UnderlinedLink target="_blank" href={link}>
		<FormattedMessage {...i18n.unsplashTermsOfServiceLink} />
	</UnderlinedLink>
);

const UnsplashLegalText = () => {
	return (
		<LegalTextWrapper>
			<FormattedMessage
				{...i18n.unsplashLegal}
				values={{
					unsplashLicenseLink: <UnsplashLicenseLink link={UNSPLASH_LICENSE_LINK} />,
					unsplashTermsOfServiceLink: (
						<UnsplashTermsOfServiceLink link={UNSPLASH_TERMS_OF_SERVICE_LINK} />
					),
				}}
			/>
		</LegalTextWrapper>
	);
};

export const ImagePickerModal = ({
	closeModal,
	openMediaBrowser,
	userOffline,
	handleUnsplashUpload,
	setIsUploadingPicture,
	initialUnsplashInfoArray,
	coverPictureState,
}: ImagePickerModalComponentProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();
	const [selectedTab, setSelectedTab] = useState<number>(0);
	const [unsplashInfoArray, setUnsplashInfoArray] =
		useState<Array<object>>(initialUnsplashInfoArray);
	const [showButtonSpinner, setShowButtonSpinner] = useState<boolean>(false);
	const [showPhotoSpinner, setShowPhotoSpinner] = useState<boolean>(false);
	const [windowWidth, setWindowWidth] = useState<number>(window.innerWidth);
	const queryWord = useRef<string>(DEFAULT_QUERY_WORD);
	const pageNumber = useRef<number>(1);

	const onResize = useCallback(() => {
		setWindowWidth(window.innerWidth);
	}, []);

	useEffect(() => {
		window.addEventListener('resize', onResize);
		return () => {
			window.removeEventListener('resize', onResize);
		};
	}, [onResize]);

	const selectTab = (index) => {
		setSelectedTab(index);
		if (index == 0) {
			document.getElementById('UnsplashSearchBarWrapper')!.scrollIntoView();
		}
		// if we select the upload tab, open up the media browser and close the modal
		if (index == 1) {
			openMediaBrowser();
			closeModal();
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'headerImageUpload',
					actionSubjectId: 'headerImageUploadTabClicked',
					source: 'editorPageCoverPicture',
				},
			}).fire();
		}
	};

	const CustomModalHeader = (props: HeaderComponentProps) => {
		return (
			<ModalHeader {...props}>
				<HeaderWrapper>
					<ModalTitle>
						<TitleDiv data-testid="modal-title">
							{formatMessage(i18n.updateImageModalTitle)}
							<ModalCloseIconWrapper onClick={closeModal}>
								<EditorCloseIcon label="editor-close-icon" />
							</ModalCloseIconWrapper>
						</TitleDiv>
					</ModalTitle>
					<Box xcss={tabsWrapperStyles}>
						<Tabs id="cover-picture-modal-tabs" selected={selectedTab} onChange={selectTab}>
							<TabList>
								<Tab>{formatMessage(i18n.searchTab)}</Tab>
								<Tab>{formatMessage(i18n.uploadTab)}</Tab>
							</TabList>
						</Tabs>
					</Box>
				</HeaderWrapper>
			</ModalHeader>
		);
	};

	const onClickLoadMoreButton = () => {
		setShowButtonSpinner(true);
		pageNumber.current++;
		void getUnsplashPhotos(queryWord.current, pageNumber.current).then((results) => {
			setUnsplashInfoArray(unsplashInfoArray.concat(results));
			setShowButtonSpinner(false);
		});
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'loadMoreUnsplashImageSelectionButtonClicked',
				source: 'editorPageCoverPicture',
			},
		}).fire();
	};

	return (
		<Modal
			onClose={closeModal}
			width={windowWidth > MAX_WINDOW_WIDTH_BEFORE_OVERFLOW ? MODAL_WIDTH : SMALL_MODAL_WIDTH}
			height={MODAL_HEIGHT}
			autoFocus={false}
		>
			<CustomModalHeader />
			<ModalBody>
				<Fragment>
					<SearchBarWrapper>
						<UnsplashSearchBar
							userOffline={userOffline}
							setUnsplashInfoArray={setUnsplashInfoArray}
							setShowPhotoSpinner={setShowPhotoSpinner}
							pageNumber={pageNumber}
							queryWord={queryWord}
						/>
					</SearchBarWrapper>
					{showPhotoSpinner ? (
						<SpinnerWrapper>
							<Spinner size="small" />
						</SpinnerWrapper>
					) : (
						<ModalContentWrapper windowWidth={windowWidth} data-testid="modal-content-wrapper">
							<UnsplashImageSelection
								unsplashInfoArray={unsplashInfoArray}
								handleUnsplashUpload={handleUnsplashUpload}
								setIsUploadingPicture={setIsUploadingPicture}
								closeModal={closeModal}
								coverPictureState={coverPictureState}
							/>
						</ModalContentWrapper>
					)}
					{/* sometimes the API doesn't return 30 images when we request 30 so just using something close like 25 to determine if there are more results to show */}
					{unsplashInfoArray.length > 25 && !showPhotoSpinner ? (
						<LoadMoreButtonWrapper>
							<LoadingButton
								testId="load-more-button"
								onClick={onClickLoadMoreButton}
								spacing="compact"
								isLoading={showButtonSpinner}
							>
								{formatMessage(i18n.loadMore)}
							</LoadingButton>
						</LoadMoreButtonWrapper>
					) : null}
				</Fragment>
			</ModalBody>
			<UnsplashLegalTerms>
				<UnsplashLegalText />
			</UnsplashLegalTerms>
		</Modal>
	);
};
